import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import type { Call, CallDirection } from 'services/types/call';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface CallsHistorySectionProps {
  clientCalls?: Call[];
}

const formatDuration = (durationSeconds: number | null): string => {
  if (durationSeconds === null || durationSeconds === undefined) {
    return '-';
  }

  if (durationSeconds < 60) {
    return `${durationSeconds}s`;
  }

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${hours}h`;
};

const CallsHistorySection: React.FC<CallsHistorySectionProps> = ({ clientCalls = undefined }) => {
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({});
  const [expandedTranscripts, setExpandedTranscripts] = useState<Record<number, boolean>>({});

  if (!clientCalls || clientCalls.length === 0) {
    return (
      <Typography
        sx={{
          color: alpha(NAVY, 0.5),
          fontStyle: 'italic',
        }}
      >
        No hay llamadas registradas para este cliente
      </Typography>
    );
  }

  const sortedCalls = [...clientCalls].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const renderCallDetails = (call: Call) => (
    <Box>
      {call.callTranscriptSummary && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 1,
              color: alpha(NAVY, 0.5),
            }}
          >
            Resumen de la conversacion
          </Typography>
          <Box
            sx={{
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_ITEM,
              p: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: NAVY,
              }}
            >
              {call.callTranscriptSummary}
            </Typography>
          </Box>
        </Box>
      )}

      {call.callTranscript && (
        <Accordion
          expanded={expandedTranscripts[call.id] || false}
          onChange={(_, isExpanded) => {
            setExpandedTranscripts((prev) => ({
              ...prev,
              [call.id]: isExpanded,
            }));
          }}
          sx={{
            mt: 1,
            boxShadow: 'none',
            borderRadius: `${RADIUS_ITEM} !important`,
            '&:before': { display: 'none' },
            backgroundColor: SURFACE_CONTAINER_LOWEST,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
            sx={{
              minHeight: 40,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: alpha(NAVY, 0.5),
              }}
            >
              Transcripcion completa
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
            <Box
              sx={{
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: alpha(NAVY, 0.7),
                  fontSize: '0.8rem',
                }}
              >
                {call.callTranscript}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );

  const getDirectionIcon = (direction: CallDirection | null) => {
    if (direction === 'USER_INITIATED') {
      return (
        <Tooltip title="Llamada entrante" arrow>
          <CallReceivedIcon fontSize="small" sx={{ color: '#47BCD8' }} />
        </Tooltip>
      );
    }
    if (direction === 'BUSINESS_INITIATED') {
      return (
        <Tooltip title="Llamada saliente" arrow>
          <CallMadeIcon fontSize="small" sx={{ color: '#53B721' }} />
        </Tooltip>
      );
    }
    return <PhoneIcon fontSize="small" sx={{ color: alpha(NAVY, 0.5) }} />;
  };

  return (
    <Box>
      {sortedCalls.map((call) => (
        <Accordion
          key={call.id}
          expanded={expandedAccordions[call.id] || false}
          onChange={(_, isExpanded) => {
            setExpandedAccordions((prev) => ({
              ...prev,
              [call.id]: isExpanded,
            }));
          }}
          sx={{
            mb: 1,
            boxShadow: 'none',
            borderRadius: `${RADIUS_CARD} !important`,
            backgroundColor: call.callStatus === 'reject' ? 'rgba(253, 237, 237, 0.92)' : SURFACE_CONTAINER_LOWEST,
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: '0 0 8px 0',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
            sx={{
              backgroundColor: call.callStatus === 'reject' ? 'rgba(253, 237, 237, 0.92)' : SURFACE_CONTAINER_LOWEST,
              borderRadius: `${RADIUS_CARD} ${RADIUS_CARD} 0 0`,
              minHeight: 48,
              overflow: 'hidden',
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
                minWidth: 0,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
              }}
            >
              {getDirectionIcon(call.callDirection)}
              {call.user && (
                <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha(NAVY, 0.5),
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    Vendedor
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                      color: NAVY,
                    }}
                  >
                    {call.user.name || `#${call.user.id}`}
                  </Typography>
                </Box>
              )}
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                {call.callDuration !== null && (
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor: alpha(NAVY, 0.06),
                      px: 1,
                      py: 0.25,
                      borderRadius: RADIUS_ITEM,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      color: NAVY,
                    }}
                  >
                    {formatDuration(call.callDuration)}
                  </Typography>
                )}
                {call.callStartTime && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha(NAVY, 0.5),
                        display: 'block',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(call.callStartTime).toLocaleDateString('es-CL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha(NAVY, 0.5),
                        display: 'block',
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(call.callStartTime).toLocaleTimeString('es-CL', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ py: 1 }}>{renderCallDetails(call)}</AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default CallsHistorySection;
